import React from 'react';
import Editor from '@monaco-editor/react';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCode } from '@fortawesome/free-solid-svg-icons';
import type { editor } from 'monaco-editor';
import { ModalWidth } from './Variables';
import './CodeEditorSheet.css';

/**
 * The code editor as it appears everywhere it appears: a framed pane with one
 * line of chrome over it naming the language, flagging read-only, and carrying
 * the one error the content can have.
 *
 * Extracted from ModalWithButton so the Submission Builder's metadata modals
 * are the same object rather than a second thing that resembles it.
 */

/** the header for a modal built around this sheet */
export const CodeModalTitle = ({ children }: { children: React.ReactNode }) => (
    <span className="njm-title">
        <span className="njm-title__ico" aria-hidden="true">
            <FontAwesomeIcon icon={faCode} />
        </span>
        {children}
    </span>
);

/**
 * The `<Modal>` props that make the sheet — spread these rather than restating
 * them, so a modal that holds code cannot drift from the others.
 */
export const codeModalProps = (title: React.ReactNode) => ({
    className: 'njm-modal',
    title: <CodeModalTitle>{title}</CodeModalTitle>,
    width: ModalWidth,
    /* centred rather than dropped 100px from the top: the sheet is tall, and
       the room above it was the last of the padding */
    centered: true,
});

export interface CodeEditorSheetProps {
    /** monaco language id; also what the chrome calls it */
    language: string;
    value: string;
    onChange?: (value: string) => void;
    onMount?: (instance: editor.IStandaloneCodeEditor) => void;
    readOnly?: boolean;
    /** the single thing that can be wrong with the content, if it is */
    error?: string;
    /** shown in the chrome when there is no error to show there */
    hint?: string;
    height?: string;
    /** what the editor announces itself as; defaults to "<language> editor" */
    ariaLabel?: string;
}

const CodeEditorSheet = ({
    language,
    value,
    onChange,
    onMount,
    readOnly = false,
    error,
    hint,
    height = '50vh',
    ariaLabel,
}: CodeEditorSheetProps) => (
    <div className={`njm-editor${error ? ' njm-editor--bad' : ''}`}>
        {/* what is being edited, and — where the eye already is rather than
            under a 50vh editor — what is wrong with it */}
        <div className="njm-editor__bar">
            <span className="njm-editor__lang">{language}</span>
            {readOnly && <span className="njm-editor__flag">Read only</span>}
            {error ? (
                <span className="njm-editor__err" role="alert">
                    <ExclamationCircleFilled aria-hidden="true" />
                    {error}
                </span>
            ) : (
                hint && <span className="njm-editor__hint">{hint}</span>
            )}
        </div>
        <Editor
            height={height}
            defaultLanguage={language}
            value={value}
            onChange={(next) => onChange?.(next ?? '')}
            onMount={onMount}
            options={{
                formatOnPaste: true,
                minimap: { enabled: false },
                readOnly,
                fontSize: 13,
                lineHeight: 21,
                padding: { top: 12, bottom: 12 },
                scrollBeyondLastLine: false,
                automaticLayout: true,
                renderLineHighlight: 'none',
                overviewRulerLanes: 0,
                ariaLabel: ariaLabel ?? `${language} editor`,
            }}
        />
    </div>
);

export default CodeEditorSheet;
